/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: IVRsFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Incoming Call Tools module.
----------------------------------------------------------------*/


import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Phone, Settings, Plus, Search, Copy, ListChecks } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { fetchIVRs, fetchIVR, createIVR, updateIVR } from './api/ivrApi';
import { IVR, IVROption, IVRSettings } from './types/ivr.types';
export type { IVROption }; // Re-export for backwards compat
import React from 'react';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModuleFormHeader } from '../../components/layouts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { navigateToIvrDuplicate, deleteIvrById } from './ivrsActions';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { IVRGeneralTab } from './tabs/IVRGeneralTab';
import { IVRSettingsTab } from './tabs/IVRSettingsTab';

const generateContextFromName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 50);

const DEFAULT_FORM_DATA = {
  name: '',
  context: '',
  welcomeMessage: '',
  instructionsMessage: '',
  invalidMessage: '',
  exitMessage: '',
  timeout: 5,
  maxFailures: 3,
  maxTimeouts: 3,
  directDial: false,
  enabled: true,
  statsEnabled: false,
  settings: {
    backgroundMusic: '',
    timeoutDestination: null as { action: string; destination: string } | null,
    failureDestination: null as { action: string; destination: string } | null,
    maxDigits: 1,
    minDigits: 1,
    digitTimeoutSound: '',
    interDigitTimeout: 5000,
    inputMethod: 'dtmf',
    speechGrammar: '',
    inputLanguage: 'en-US',
    beepBeforeInput: false,
    maxCallDuration: 1800,
    recordSession: false,
    logLevel: 'info',
    variables: {} as Record<string, unknown>
  } as IVRSettings,
  options: [] as IVROption[]
};

export default function IVRsFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(id && id !== 'new');
  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();
  const lastSaveTimestamp = useRef<number>(0);

  const lastLoadedIdRef = useRef<string | null>(null);
  const isLoadingRef = useRef<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);
  const currentIdRef = useRef<string | undefined>(undefined);
  const hasLoadedInitialDataRef = useRef<boolean>(false);
  const lastIdRef = useRef<string | undefined>(id);
  const lastResetParamRef = useRef<string | null>(null);

  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);
  const showInfoRef = useRef(showInfo);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    showErrorRef.current = showError;
    showSuccessRef.current = showSuccess;
    showInfoRef.current = showInfo;
    tRef.current = t;
    navigateRef.current = navigate;
  }, [showError, showSuccess, showInfo, t, navigate]);

  // Reset form when navigating to 'new' after Save and New
  useEffect(() => {
    const currentId = id || 'new';
    const previousId = lastIdRef.current;
    const currentResetParam = resetParam;

    const shouldReset = (!id || id === 'new') &&
      !isEditMode &&
      (previousId !== currentId ||
        (currentResetParam && currentResetParam !== lastResetParamRef.current));

    if (shouldReset) {
      setFormData(DEFAULT_FORM_DATA);
      setOriginalData({ ...DEFAULT_FORM_DATA, settings: {} as Record<string, unknown> });
      setErrors({});
      if (currentResetParam) {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('_reset');
        const newSearch = newSearchParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
      }
    }

    lastIdRef.current = currentId;
    lastResetParamRef.current = currentResetParam;
  }, [id, isEditMode, resetParam, searchParams]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);
  const [availableIVRs, setAvailableIVRs] = useState<IVR[]>([]);
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [originalData, setOriginalData] = useState<typeof DEFAULT_FORM_DATA & { settings: Record<string, any> }>({ ...DEFAULT_FORM_DATA, settings: {} as Record<string, unknown> });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadIVRs = useCallback(async () => {
    try {
      const result = await fetchIVRs({ page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' }, undefined, 'name');
      if (result.success && Array.isArray(result.data)) setAvailableIVRs(result.data);
    } catch {
      showErrorRef.current(tRef.current('ivrs:messages.loadError'));
    }
  }, []);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) return availableIVRs;
    return availableIVRs.filter((item) => {
      const name = (item.name ?? '').toString().toLowerCase();
      const context = (item.context ?? '').toString().toLowerCase();
      const description = (item.description ?? '').toString().toLowerCase();
      return name.includes(query) || context.includes(query) || description.includes(query);
    });
  }, [availableIVRs, quickListSearch]);

  const loadIVRFn = useCallback(async (ivrId: string, isDuplicate = false) => {
    if (isLoadingRef.current || lastLoadedIdRef.current === ivrId) return;
    if (!ivrId || ivrId === 'new') { setIsLoading(false); isLoadingRef.current = false; return; }

    setIsLoading(true);
    isLoadingRef.current = true;
    lastLoadedIdRef.current = ivrId;

    try {
      const ivr = await fetchIVR(ivrId);
      const settings: IVRSettings = (ivr.settings || {}) as IVRSettings;

      const loadedData = {
        name: isDuplicate ? `${ivr.name} (Copy)` : ivr.name,
        context: ivr.context || generateContextFromName(isDuplicate ? `${ivr.name} (Copy)` : ivr.name),
        welcomeMessage: ivr.welcomeRecordingId ? String(ivr.welcomeRecordingId) : (ivr.welcomeMessage || ''),
        instructionsMessage: ivr.instructionsRecordingId ? String(ivr.instructionsRecordingId) : (ivr.instructionsMessage || ''),
        invalidMessage: ivr.invalidRecordingId ? String(ivr.invalidRecordingId) : (ivr.invalidMessage || ''),
        exitMessage: ivr.exitRecordingId ? String(ivr.exitRecordingId) : (ivr.exitMessage || ''),
        timeout: ivr.timeout ?? 5,
        maxFailures: ivr.maxFailures ?? 3,
        maxTimeouts: ivr.maxTimeouts ?? 3,
        directDial: ivr.directDial ?? false,
        enabled: ivr.enabled ?? true,
        statsEnabled: ivr.statsEnabled ?? false,
        settings: {
          backgroundMusic: settings.backgroundMusic || '',
          timeoutDestination: settings.timeoutDestination || null,
          failureDestination: settings.failureDestination || null,
          maxDigits: settings.maxDigits ?? 1,
          minDigits: settings.minDigits ?? 1,
          digitTimeoutSound: settings.digitTimeoutSound || '',
          interDigitTimeout: settings.interDigitTimeout ?? 5000,
          inputMethod: settings.inputMethod || 'dtmf',
          speechGrammar: settings.speechGrammar || '',
          inputLanguage: settings.inputLanguage || 'en-US',
          beepBeforeInput: settings.beepBeforeInput ?? false,
          maxCallDuration: settings.maxCallDuration ?? 1800,
          recordSession: settings.recordSession ?? false,
          logLevel: settings.logLevel || 'info',
          variables: settings.variables || {},
        } as IVRSettings,
        options: (ivr.options || []).map((opt: IVROption) => ({
          id: opt.id,
          digits: opt.digits,
          action: opt.action,
          destination: opt.destination || '',
          condition: opt.condition || '',
          breakOnMatch: opt.breakOnMatch ?? false,
          priority: opt.priority ?? 100,
          enabled: opt.enabled ?? true,
          settings: opt.settings || {},
        })),
      };

      setTimeout(() => { setFormData(loadedData); setOriginalData(loadedData); }, 0);
    } catch (error) {
      lastLoadedIdRef.current = null;
      showErrorRef.current(error instanceof Error ? error.message : tRef.current('ivrs:messages.loadError'));
      navigateRef.current('/pbx/incoming-tools/ivr');
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, []);

  const loadIVRRef = useRef(loadIVRFn);
  useEffect(() => { loadIVRRef.current = loadIVRFn; }, [loadIVRFn]);

  useEffect(() => {
    if (!hasLoadedInitialDataRef.current) { hasLoadedInitialDataRef.current = true; loadIVRs(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { return () => { lastLoadedIdRef.current = null; currentIdRef.current = undefined; }; }, []);

  useEffect(() => {
    const loadId = duplicateId || id;
    const idChanged = currentIdRef.current !== loadId;
    if (!idChanged) return;
    currentIdRef.current = loadId;

    if (duplicateId && lastLoadedIdRef.current !== duplicateId && !isLoadingRef.current) {
      setTimeout(() => loadIVRRef.current(duplicateId, true), 0);
    } else if (isEditMode && id && id !== 'new' && lastLoadedIdRef.current !== id && !isLoadingRef.current) {
      setTimeout(() => loadIVRRef.current(id, false), 0);
    } else if (!isEditMode && !duplicateId) {
      lastLoadedIdRef.current = null;
      if (!hasInitializedRef.current) {
        setTimeout(() => { hasInitializedRef.current = true; setIsLoading(false); isLoadingRef.current = false; }, 0);
      }
    }
  }, [id, duplicateId, isEditMode]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('ivrs:validation.nameRequired');
    if (formData.timeout < 1 || formData.timeout > 60) newErrors.timeout = t('ivrs:validation.timeoutRequired');
    if (formData.maxFailures < 1 || formData.maxFailures > 10) newErrors.maxFailures = t('ivrs:validation.maxFailuresRequired');
    if (formData.maxTimeouts < 1 || formData.maxTimeouts > 10) newErrors.maxTimeouts = t('ivrs:validation.maxTimeoutsRequired');

    formData.options.forEach((option, index) => {
      if (!option.digits.trim()) newErrors[`option_${index}_digits`] = t('ivrs:validation.optionDigitsRequired');
      if (!option.action) newErrors[`option_${index}_action`] = t('ivrs:validation.optionActionRequired');
      if ((option.action === 'transfer' || option.action === 'goto') && !option.destination.trim())
        newErrors[`option_${index}_destination`] = t('ivrs:validation.optionDestinationRequired');
    });

    if (formData.settings.maxDigits < 1 || formData.settings.maxDigits > 10) newErrors.maxDigits = t('ivrs:validation.maxDigitsRequired');
    if (formData.settings.minDigits < 1 || formData.settings.minDigits > 10) newErrors.minDigits = t('ivrs:validation.minDigitsRequired');
    if (formData.settings.minDigits > formData.settings.maxDigits) newErrors.minDigits = t('ivrs:messages.minDigitsGreaterThanMax');
    if (formData.settings.interDigitTimeout < 1000 || formData.settings.interDigitTimeout > 10000) newErrors.interDigitTimeout = t('ivrs:validation.interDigitTimeoutRequired');
    if (formData.settings.maxCallDuration < 30 || formData.settings.maxCallDuration > 3600) newErrors.maxCallDuration = t('ivrs:validation.maxCallDurationRequired');

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleSubmit = async (e?: React.FormEvent): Promise<IVR | undefined> => {
    if (e) e.preventDefault();
    const validation = validateForm();
    if (!validation.isValid) {
      const errorMsgs = Object.values(validation.errors).filter(msg => typeof msg === 'string').join(' � ');
      showError(`${t('ivrs:messages.fixValidationErrors')}${errorMsgs ? ': ' + errorMsgs : ''}`);
      return undefined;
    }

    setIsSaving(true);
    try {
      const now = Date.now();
      if (now - lastSaveTimestamp.current < 1000) return undefined;
      lastSaveTimestamp.current = now;

      const generatedContext = formData.context || generateContextFromName(formData.name);
      const settings: Record<string, unknown> = {
        background_music: formData.settings.backgroundMusic || null,
        timeout_destination: formData.settings.timeoutDestination || null,
        failure_destination: formData.settings.failureDestination || null,
        max_digits: formData.settings.maxDigits, min_digits: formData.settings.minDigits,
        digit_timeout_sound: formData.settings.digitTimeoutSound,
        inter_digit_timeout: formData.settings.interDigitTimeout,
        input_method: formData.settings.inputMethod, speech_grammar: formData.settings.speechGrammar || null,
        input_language: formData.settings.inputLanguage, beep_before_input: formData.settings.beepBeforeInput,
        max_call_duration: formData.settings.maxCallDuration, record_session: formData.settings.recordSession,
        log_level: formData.settings.logLevel, variables: formData.settings.variables || {}
      };
      const options = formData.options.map(opt => ({
        digits: opt.digits, action: opt.action, destination: opt.destination || null,
        condition: opt.condition || null, breakOnMatch: opt.breakOnMatch,
        priority: opt.priority, enabled: opt.enabled, settings: opt.settings || {}
      }));

      const dataToSave = {
        name: formData.name, description: null, context: generatedContext,
        welcomeMessage: formData.welcomeMessage || null, instructionsMessage: formData.instructionsMessage || null,
        invalidMessage: formData.invalidMessage || null, exitMessage: formData.exitMessage || null,
        timeout: formData.timeout, maxFailures: formData.maxFailures, maxTimeouts: formData.maxTimeouts,
        directDial: formData.directDial, enabled: formData.enabled, statsEnabled: formData.statsEnabled,
        settings, options
      };

      const result = isEditMode && id ? await updateIVR(id, dataToSave) : await createIVR(dataToSave);
      showSuccess(isEditMode ? t('ivrs:messages.saveSuccessUpdate') : t('ivrs:messages.saveSuccessCreate'));

      setOriginalData({ ...formData, context: generatedContext });
      lastLoadedIdRef.current = null;
      currentIdRef.current = undefined;
      return result;
    } catch (error) {
      showError(error instanceof Error ? error.message : t('ivrs:messages.saveError'));
      return undefined;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();
    if (!savedData && isEditMode && id) return { id: Number(id), uuid: String(id) } as IVR;
    return savedData;
  }, [handleSubmit, isEditMode, id]);

  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/incoming-tools/ivr',
    newPath: '/pbx/incoming-tools/ivr/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => `/pbx/incoming-tools/ivr/${savedData.uuid || savedData.id}`,
    getFormData: () => ({ formData }),
    storageKey: 'ivrs:saveAndNewData'
  });

  const handleDeleteClick = () => setShowDeleteModal(true);
  const handleDeleteConfirm = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await deleteIvrById(id);
      showSuccess(t('ivrs:messages.deleteSuccess'));
      navigate('/pbx/incoming-tools/ivr');
    } catch (error) {
      showError(error instanceof Error ? error.message : t('ivrs:messages.deleteError'));
    } finally { setIsDeleting(false); setShowDeleteModal(false); }
  };

  const hasChanges = useMemo(() => JSON.stringify(formData) !== JSON.stringify(originalData), [formData, originalData]);
  const handleCancel = () => { if (isEditMode) { setFormData({ ...originalData }); setErrors({}); } else navigate('/pbx/incoming-tools/ivr'); };
  const handleDuplicateFromForm = () => { if (!isEditMode || !id) return; navigateToIvrDuplicate(navigate, id); };

  const handleAddOption = () => {
    const newOption: IVROption = {
      digits: '', action: 'transfer', destination: '', condition: '', breakOnMatch: false,
      priority: formData.options.length > 0 ? Math.max(...formData.options.map(o => o.priority)) + 10 : 100,
      enabled: true, settings: {}
    };
    setFormData(prev => ({ ...prev, options: [...prev.options, newOption] }));
    setEditingOptionIndex(formData.options.length);
  };

  const handleUpdateOption = (index: number, option: IVROption) => {
    setFormData(prev => { const opts = [...prev.options]; opts[index] = option; return { ...prev, options: opts }; });
    setEditingOptionIndex(null);
  };

  const handleDeleteOption = (index: number) => {
    setFormData(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== index) }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<Phone className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('ivrs:editTitle')} - ${formData.name || ''}` : t('ivrs:createTitle')}
        backButtonText={t('ivrs:actions.back')}
        onBack={() => navigate('/pbx/incoming-tools/ivr')}
        actions={[
          { icon: <Search className="h-5 w-5 text-muted-foreground" />, tooltip: t('ivrs:quickList.tooltip', 'Search'), onClick: () => { setShowQuickList(true); if (!availableIVRs.length) void loadIVRs(); } },
          ...(isEditMode ? [{ icon: <Copy className="h-5 w-5 text-muted-foreground" />, tooltip: t('common.duplicate'), onClick: handleDuplicateFromForm }] : []),
          { icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('ivrs:actions.add'), onClick: () => navigate('/pbx/incoming-tools/ivr/new') },
        ]}
      />

      <div className="mt-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1">
            <TabsTrigger value="general">{t('ivrs:tabs.general')}</TabsTrigger>
            <TabsTrigger value="settings">{t('ivrs:tabs.settings')}</TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <IVRGeneralTab
              formData={formData}
              setFormData={setFormData as React.Dispatch<React.SetStateAction<typeof formData>>}
              errors={errors}
              availableIVRs={availableIVRs}
              editingOptionIndex={editingOptionIndex}
              setEditingOptionIndex={setEditingOptionIndex}
              onAddOption={handleAddOption}
              onUpdateOption={handleUpdateOption}
              onDeleteOption={handleDeleteOption}
              onSubmit={handleSubmit}
              generateContextFromName={generateContextFromName}
            />
          </TabsContent>

          <TabsContent value="settings">
            <IVRSettingsTab
              formData={formData}
              setFormData={setFormData as React.Dispatch<React.SetStateAction<typeof formData>>}
              errors={errors}
            />
          </TabsContent>
        </Tabs>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? handleDeleteClick : undefined}
        isEditMode={isEditMode}
        isSaving={isSaving}
        isLoading={isLoading}
        saveText={t('ivrs:actions.save')}
        cancelText={t('ivrs:actions.cancel')}
        deleteText={t('ivrs:actions.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="ivrs:saveActionPreference"
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('ivrs:messages.deleteTitle')}
        message={t('ivrs:messages.confirmDelete', { name: formData.name })}
        confirmText={t('ivrs:actions.delete')}
        cancelText={t('ivrs:actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => { setShowQuickList(false); setQuickListSearch(''); }}
        title={t('ivrs:quickList.title', 'IVRs')}
        items={filteredQuickList}
        isLoadingInitial={false}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => { setShowQuickList(false); setQuickListSearch(''); navigate(`/pbx/incoming-tools/ivr/${item.uuid ?? item.id}`, { replace: true }); }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button type="button" disabled={isCurrent}
            onClick={() => { if (isCurrent) return; setShowQuickList(false); setQuickListSearch(''); navigate(`/pbx/incoming-tools/ivr/${item.uuid ?? item.id}`, { replace: true }); }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60' : 'hover:bg-muted/40'}`}>
            <div className="font-medium text-foreground">{item.name}</div>
            {item.context && <div className="text-xs text-muted-foreground mt-0.5">{item.context}</div>}
            {item.description && <div className="text-xs text-muted-foreground mt-0.5">{item.description}</div>}
          </button>
        )}
        searchPlaceholder={t('ivrs:quickList.searchPlaceholder', 'Search by name, context or description')}
        emptyMessage={t('ivrs:quickList.empty', 'No IVRs found')}
        headerIcon={<Phone className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
